import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Bot, BotOff, AlertTriangle } from 'lucide-react'

type BotHealth = {
  service: string
  last_heartbeat: string
  status: string
  last_error: string | null
  last_error_at: string | null
  processed_count: number
  error_count: number
}

export function BotStatusBadge() {
  const { data, isLoading } = useQuery({
    queryKey: ['bot-health'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_health')
        .select('*')
        .eq('service', 'supplement_bot')
        .single()

      if (error || !data) return { online: false, hasErrors: false, status: 'unknown' as const }

      const health = data as BotHealth
      const lastBeat = new Date(health.last_heartbeat)
      const minutesAgo = (Date.now() - lastBeat.getTime()) / 60_000

      // Bot is "live" if heartbeat within last 10 minutes and status is connected
      const online = minutesAgo < 10 && health.status === 'connected'
      // Has recent errors if error in last hour
      const hasErrors = health.last_error_at
        ? (Date.now() - new Date(health.last_error_at).getTime()) < 3_600_000
        : false

      return { online, hasErrors, status: health.status, minutesAgo: Math.round(minutesAgo), lastError: health.last_error, errorCount: health.error_count, processedCount: health.processed_count }
    },
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  })

  if (isLoading) return null

  const online = data?.online ?? false
  const hasErrors = data?.hasErrors ?? false

  // Three states: live (green), live with errors (amber), offline (red)
  if (online && !hasErrors) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
        title={`Bot connected | Processed: ${data?.processedCount ?? 0} | Errors: ${data?.errorCount ?? 0}`}>
        <Bot className="w-3 h-3" />
        <span>Bot Live</span>
      </div>
    )
  }

  if (online && hasErrors) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
        title={`Bot connected with errors | Last error: ${data?.lastError || 'unknown'}`}>
        <AlertTriangle className="w-3 h-3" />
        <span>Bot Errors</span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200"
      title={`Bot offline | Last seen: ${data?.minutesAgo ?? '?'}m ago | Status: ${data?.status ?? 'unknown'}`}>
      <BotOff className="w-3 h-3" />
      <span>Bot Offline</span>
    </div>
  )
}
